import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MyContext } from '../../Context/MyProvider';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaBriefcase, FaHeart, FaRegFolderOpen, FaTicketAlt, FaWpforms } from "react-icons/fa";
import { FiBell, FiHeadphones, FiLogOut, FiMapPin, FiRefreshCcw, FiSearch, FiShield, FiStar } from "react-icons/fi";
import { HiOutlineFilm, HiOutlineHome } from "react-icons/hi2";
import { MdOutlineAccountBalanceWallet, MdOutlineApps, MdOutlineEvent, MdOutlineLocalOffer, MdOutlineShoppingBag, MdOutlineSportsCricket } from "react-icons/md";
import { BsController } from "react-icons/bs";
import logoWithName from "../../Images/logowithname.png";
import DesktopUserChip from "./DesktopUserChip";
import { showTost } from '../../showTost';
import { isUserLoggedIn, logoutUser, requireLogin } from "../../utils/auth";
import { arrangeSidebarMenuItems } from "../../utils/sidebarMenu";

const FormList = ({ variant = "page" }) => {
    const { BaseUrl, setIsMegaMenu, setIsLoginShow, setIsLogin, setIsShowBtn } = useContext(MyContext);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [screenLimit, setScreenLimit] = useState(4);
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (variant !== "page") {
            return undefined;
        }

        document.body.classList.add("desktop-home-active");
        return () => {
            document.body.classList.remove("desktop-home-active");
        };
    }, [variant]);

    useEffect(() => {
        const getFormData = async () => {
            setLoading(true);
            try {
                const res = await axios.post(`${BaseUrl}user_api/form_list.php`);

                if (res?.data?.status) {
                    const filteredData = (res.data.data || []).filter(
                        (item) => item.form_type === null || item.form_type === "user"
                    );

                    setData(filteredData);
                } else {
                    setData([])
                }
            } catch (error) {
                setData([])
            } finally {
                setLoading(false);
            }
        };

        getFormData();
    }, [BaseUrl]);

    useEffect(() => {
        const updateLimit = () => {
            const width = window.innerWidth;

            if (width < 640) {
                setScreenLimit(1); // mobile
            } else if (width < 768) {
                setScreenLimit(1); // sm
            } else if (width < 1024) {
                setScreenLimit(2); // md
            } else if (width < 1200) {
                setScreenLimit(3); // lg
            } else if (width < 1440) {
                setScreenLimit(4); // lg
            } else {
                setScreenLimit(4); // xl and above
            }
        };

        updateLimit();
        window.addEventListener("resize", updateLimit);

        return () => window.removeEventListener("resize", updateLimit);
    }, []);

    const onForm = (item) => {
        navigate(`/form/${item?.slug}`);
        setIsMegaMenu(false);
    };

    const visibleForms = useMemo(() => {
        if (variant === "header") {
            return data.slice(0, screenLimit);
        }
        return data;
    }, [data, screenLimit, variant]);

    const isEmpty = !visibleForms || visibleForms.length === 0;
    const formMenuItems = [
        { label: "Home", icon: HiOutlineHome, path: "/" },
        { label: "Movies", icon: HiOutlineFilm },
        { label: "Events", icon: MdOutlineEvent, path: "/events" },
        { label: "Sports", icon: MdOutlineSportsCricket },
        { label: "Games", icon: BsController },
        { label: "Shopping", icon: MdOutlineShoppingBag, externalUrl: "https://shop.nabhiverse.com/" },
        { label: "Services", icon: MdOutlineApps },
        { label: "Offers", icon: MdOutlineLocalOffer },
        { label: "Bookings", icon: FaTicketAlt, path: "/booking" },
        { label: "Favorites", icon: FaHeart, path: "/favorite" },
        { label: "eWallet", icon: MdOutlineAccountBalanceWallet, path: "/wallet" },
        { label: "Forms", icon: FaWpforms, path: "/form-list", active: true },
        { label: "Business", icon: FaBriefcase, path: "/business-user" },
        { label: "More", icon: MdOutlineApps },
        { label: "Logout", icon: FiLogOut, action: "logout" },
    ];
    const mobileBottomMenuItems = [
        { label: "Home", icon: HiOutlineHome, path: "/" },
        { label: "Forms", icon: FaWpforms, path: "/form-list", active: true },
        { label: "Business", icon: FaBriefcase, path: "/business-user" },
        { label: "More", icon: MdOutlineApps, action: () => setIsMobileDrawerOpen(true) },
    ];
    const handleMenuClick = async (item) => {
        if (item.externalUrl) {
            if (!requireLogin(setIsLoginShow)) return;

            window.location.href = item.externalUrl;
            setIsMobileDrawerOpen(false);
            return;
        }

        if (item.action === "logout") {
            const didLogout = await logoutUser({ navigate, setIsLogin, setIsShowBtn });

            if (didLogout) {
                showTost("Logout successfully !!", "success");
            }

            setIsMobileDrawerOpen(false);
            return;
        }

        if (item.path) {
            navigate(item.path);
            setIsMobileDrawerOpen(false);
        }
    };
    const shouldShowMenuItem = (item) =>
        !["Bookings", "Favorites", "eWallet", "Logout"].includes(item.label) ||
        isUserLoggedIn();
    const renderFormContent = () => (
        <section className="form_list_shell">
            <div className="form_list_hero">
                <div>
                    <span className="form_list_eyebrow">NabhiVerse Forms</span>
                    <h1>Forms</h1>
                    <p>Choose the right form and continue your registration, application, or request in a few taps.</p>
                </div>
                <div className="form_list_count">
                    <FaWpforms />
                    <span>Available Forms</span>
                    <strong>{loading ? "--" : visibleForms.length}</strong>
                </div>
            </div>

            {loading ? (
                <div className="form_list_grid">
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                        <div key={item} className="form_list_card form_list_card_skeleton">
                            <span></span>
                            <div></div>
                            <p></p>
                        </div>
                    ))}
                </div>
            ) : isEmpty ? (
                <div className="form_list_empty">
                    <FaRegFolderOpen />
                    <h2>No forms available</h2>
                    <p>Please check back later.</p>
                </div>
            ) : (
                <ul className="form_list_grid">
                    {visibleForms.map((item, i) => (
                        <li key={i}>
                            <button type="button" onClick={() => onForm(item)} className="form_list_card">
                                <span className="form_list_icon">
                                    {item?.logo ? (
                                        <img src={item.logo} alt={item?.title || "form-logo"} />
                                    ) : (
                                        <FaWpforms />
                                    )}
                                </span>
                                <strong>{item?.title}</strong>
                                <small>Open form <FaArrowRight /></small>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
    if (variant === "header") {
        if (loading || isEmpty) {
            return (
                <div className="w-full flex flex-col items-center justify-center py-6 text-gray-400">
                    <FaRegFolderOpen className="text-[28px] mb-2 opacity-70" />
                    <p className="text-xs">{loading ? "Loading forms..." : "No forms available"}</p>
                </div>
            );
        }

        return (
            <div className="w-full ">
                <ul className="flex items-center gap-2 sm:gap-3 md:gap-4 lg:gap-2 overflow-x-auto scrollbar-hide">
                    {visibleForms.map((item, i) => (
                        <li
                            key={i}
                            onClick={() => onForm(item)}
                            className="min-w-0 shrink-0 flex items-center gap-2 border rounded-lg bg-[#515151] px-2 py-2 sm:px-3 lg:px-2 cursor-pointer hover:shadow transition"
                        >
                            <img
                                src={item?.logo}
                                alt={item?.title || "form-logo"}
                                className="w-[38px] h-[38px] sm:w-[45px] sm:h-[45px] object-contain flex-shrink-0"
                            />
                            <span className="text-[11px] sm:text-xs md:text-[0.7rem] leading-tight text-white max-w-[90px] sm:max-w-[120px] line-clamp-2">
                                {item?.title}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    if (variant === "mega") {
        if (loading || isEmpty) {
            return (
                <div className="w-full flex flex-col items-center justify-center py-8 text-gray-300 bg-[#101121] rounded-lg border border-[#312554]">
                    <FaRegFolderOpen className="text-[30px] mb-2 opacity-80" />
                    <p className="text-sm">{loading ? "Loading forms..." : "No forms available"}</p>
                </div>
            );
        }

        return (
            <div className="w-full mt-4 sm:mt-0 flex justify-center">
                <div className="w-[95%] sm:w-full px-3 sm:px-4 md:px-6 py-3 bg-[#101121] border border-[#312554] rounded-lg">
                    <div className="text-center mt-4 mb-6 md:hidden">
                        <h2 className="text-[22px] font-semibold text-white">Forms</h2>
                        <div className="w-[40px] h-[3px] bg-[#8c26ff] mx-auto mt-2 rounded-full"></div>
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                        {visibleForms.map((item, i) => (
                            <li
                                key={i}
                                onClick={() => onForm(item)}
                                className="flex items-center p-3 border border-[#302554] rounded-lg bg-[#14162a] text-white hover:text-[#d75dff] hover:border-[#8c26ff] hover:shadow transition gap-3 cursor-pointer min-w-0"
                            >
                                {item?.logo ? (
                                    <img
                                        src={item.logo}
                                        alt={item?.title || "form-logo"}
                                        className="w-[45px] h-[45px] sm:w-[50px] sm:h-[50px] object-contain flex-shrink-0"
                                    />
                                ) : (
                                    <FaWpforms className="w-[36px] h-[36px] flex-shrink-0 text-[#d75dff]" />
                                )}
                                <span className="text-xs sm:text-sm leading-snug break-words">
                                    {item?.title}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    }

    return (
        <div className="nabhiverse_desktop_home form_list_layout">
            <aside className="desktop_home_sidebar">
                <div className="desktop_home_logo">
                    <img src={logoWithName} alt="Nabhiverse" />
                </div>
                <nav className="desktop_home_nav">
                    {arrangeSidebarMenuItems(formMenuItems).filter(shouldShowMenuItem).map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.label}
                                className={`desktop_home_nav_item ${item.active ? "active" : ""}`}
                                type="button"
                                onClick={() => handleMenuClick(item)}
                            >
                                <Icon />
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </nav>
                <div className="desktop_home_star_box">
                    <FiStar />
                    <span>nabi star</span>
                    <strong>Your Stars 620</strong>
                    <button type="button">View Benefits</button>
                </div>
                <div className="desktop_home_help_box">
                    <FiHeadphones />
                    <span>Need Help?</span>
                    <p>24x7 Support</p>
                    <button type="button">Contact Us</button>
                </div>
            </aside>

            <div
                className={`mobile_menu_overlay ${isMobileDrawerOpen ? "show" : ""}`}
                onClick={() => setIsMobileDrawerOpen(false)}
            ></div>
            <aside className={`mobile_menu_drawer ${isMobileDrawerOpen ? "show" : ""}`}>
                <div className="mobile_drawer_head">
                    <img src={logoWithName} alt="Nabhiverse" />
                    <button type="button" onClick={() => setIsMobileDrawerOpen(false)}>×</button>
                </div>
                <div className="mobile_drawer_body">
                    <nav className="mobile_drawer_nav">
                        {arrangeSidebarMenuItems(formMenuItems).filter(shouldShowMenuItem).map((item) => {
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.label}
                                    className={`desktop_home_nav_item ${item.active ? "active" : ""}`}
                                    type="button"
                                    onClick={() => handleMenuClick(item)}
                                >
                                    <Icon />
                                    <span>{item.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                    <div className="desktop_home_star_box">
                        <FiStar />
                        <span>nabi star</span>
                        <strong>Your Stars 620</strong>
                        <button type="button">View Benefits</button>
                    </div>
                    <div className="desktop_home_help_box">
                        <FiHeadphones />
                        <span>Need Help?</span>
                        <p>24x7 Support</p>
                        <button type="button">Contact Us</button>
                    </div>
                </div>
            </aside>

            <main className="desktop_home_main form_list_main">
                <header className="desktop_home_topbar form_list_topbar">
                    <div className="mobile_header_logo">
                        <img src={logoWithName} alt="Nabhiverse" />
                    </div>
                    <div className="desktop_search_box">
                        <FiSearch />
                        <input
                            type="text"
                            placeholder="Search for apps, services, movies, events and more..."
                            readOnly
                        />
                    </div>
                    <div className="desktop_location"><FiMapPin /> Bengaluru</div>
                    <DesktopUserChip />
                    <button className="desktop_bell_btn" type="button" aria-label="Notifications">
                        <FiBell />
                        <span>3</span>
                    </button>
                </header>
                {renderFormContent()}
                <footer className="desktop_home_footer form_list_footer">
                    <img src={logoWithName} alt="Nabhiverse" />
                    <span><FiShield /> Fast & Secure Payments</span>
                    <span><FiRefreshCcw /> Easy Returns</span>
                    <span><FiHeadphones /> 24x7 Support</span>
                    <p>© 2026 Nabhiverse. All Rights Reserved.</p>
                </footer>
            </main>

            <nav className="mobile_bottom_menu">
                {mobileBottomMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                        <button
                            key={item.label}
                            type="button"
                            className={item.active ? "active" : ""}
                            onClick={() => item.action ? item.action() : navigate(item.path)}
                        >
                            <Icon />
                            <span>{item.label}</span>
                        </button>
                    );
                })}
            </nav>
        </div>
    );
};

export default FormList;
